/* Knot/Not Google extra events v1 — uses the existing Google & YouTube tag.
 * No tag loader, config, page_view, purchase, customer data, or Meta changes.
 * Uses the existing native Google tag and its current client/session.
 */
(() => {
  'use strict';
  if (window.__knotnotGoogleExtra || window.Shopify?.designMode) return;
  window.__knotnotGoogleExtra = true;
  const MID = 'G-EXNQ71REFJ';
  const debug = new URLSearchParams(location.search).get('kn_ga_debug') === '1';
  const PREFIX = 'kn_ga_extra_v1:';
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
  const path = () => location.origin + location.pathname;
  const read = key => { try { return JSON.parse(sessionStorage.getItem(PREFIX + key)); } catch (_) { return null; } };
  const remove = key => { try { sessionStorage.removeItem(PREFIX + key); } catch (_) {} };
  const write = (key, value) => { try { sessionStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch (_) {} };
  const uid = () => crypto.randomUUID();
  const documentId = uid();
  const pending = new Map();
  function send(name, fields, id = uid(), attempt = 0) {
    if (!allowed()) { pending.delete(id); return; }
    const tag = typeof window.gtag === 'function' ? window.gtag :
      Array.isArray(window.dataLayer) ? function () { window.dataLayer.push(arguments); } : null;
    if (!tag) {
      if (attempt < 20) {
        const timer = setTimeout(() => { pending.delete(id); send(name, fields, id, attempt + 1); }, 250);
        pending.set(id, timer);
      }
      return;
    }
    if (debug) console.info('KNOTNOT_GOOGLE_EXTRA', name, JSON.stringify(fields));
    tag('event', name, {
      ...fields, send_to: MID, event_id: id,
      page_location: path(), integration: 'knotnot_google_extra_v1',
      ...(debug ? {event_callback: () => console.info('KNOTNOT_GOOGLE_EXTRA_DISPATCHED', name)} : {})
    });
  }
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) {
      pending.forEach(clearTimeout); pending.clear();
      remove('newsletter_signup'); remove('generate_lead'); remove('account_newsletter');
    } else { checkSuccess(); }
  });

  // Emit once for a closed -> open transition, not cart changes while open.
  const attached = new WeakSet();
  function attachDrawers() {
    document.querySelectorAll('cart-drawer').forEach(drawer => {
      if (attached.has(drawer)) return;
      attached.add(drawer);
      let open = drawer.classList.contains('open');
      let sequence = 0;
      new MutationObserver(async () => {
        const next = drawer.classList.contains('open');
        if (next === open) return;
        open = next; const current = ++sequence;
        if (!next || !allowed()) return;
        const eventId = uid();
        try {
          const cartPath = new URL((window.Shopify?.routes?.root || '/') + 'cart.js', location.origin);
          const response = await fetch(cartPath.href, { credentials: 'same-origin' });
          if (!response.ok) return;
          const cart = await response.json();
          if (current !== sequence || !drawer.isConnected || !drawer.classList.contains('open') || !allowed()) return;
          if (!Array.isArray(cart.items) || !/^[A-Z]{3}$/.test(cart.currency) || !Number.isFinite(cart.total_price)) return;
          const items = cart.items.flatMap(item => {
            const product = String(item.product_id), variant = String(item.variant_id);
            if (!/^\d+$/.test(product) || !/^\d+$/.test(variant) || !Number.isInteger(item.quantity) || item.quantity <= 0) return [];
            const out = {
              item_id: `shopify_ZZ_${product}_${variant}`,
              item_name: String(item.product_title || '').slice(0, 100),
              item_variant: String(item.variant_title || '').slice(0, 100),
              quantity: item.quantity
            };
            // Allocated line value reflects line and order discounts, in major units.
            if (Number.isFinite(item.final_line_price)) out.price = item.final_line_price / 100 / item.quantity;
            return [out];
          });
          send('view_cart', { cart_surface: 'drawer', value: cart.total_price / 100, currency: cart.currency, items }, eventId);
        } catch (_) { /* Never interrupt the cart. */ }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // Metadata is rendered from the exact Liquid product, never inferred from text.
  document.addEventListener('click', event => {
    if (!allowed() || event.button !== 0 || event.defaultPrevented) return;
    const anchor = event.target.closest?.('product-card a[href]');
    if (!anchor || event.target.closest('button, input, select, quick-view-button, add-to-cart')) return;
    const card = anchor.closest('product-card[data-kn-ga-item]');
    if (!card) return;
    try {
      const destination = new URL(anchor.href, location.href);
      if (destination.origin !== location.origin || !destination.pathname.includes('/products/')) return;
      const item = JSON.parse(card.dataset.knGaItem);
      const variantId = destination.searchParams.get('variant') || String(item.default_variant_id);
      const variant = item.variants?.find(v => String(v.id) === variantId);
      if (!variant || !/^\d+$/.test(String(item.product_id)) || typeof item.item_name !== 'string') return;
      const list = location.pathname;
      // Explicit allowlist: no arbitrary DOM/form content is forwarded.
      send('select_item', { item_list_id: list, items: [{
        item_id: `shopify_ZZ_${item.product_id}_${variantId}`, item_name: item.item_name,
        item_variant: variant.title, price: variant.price, quantity: 1
      }] });
    } catch (_) {}
  });

  // Membership invitation and native sign-in return. Never infer a new subscriber.
  const accountState = () => document.querySelector('script[data-kn-ga-auth]')?.dataset;
  const accountSurfaces = ['membership_popup', 'account_login'];
  const accountMethods = ['email', 'google', 'facebook', 'unknown'];
  function accountRecord(surface, method, attempt) {
    if (accountState()?.knGaAuth !== 'false') return;
    write('account_newsletter', { id: uid(), time: Date.now(), surface, method, attempt });
  }
  const membershipObserved = new WeakSet();
  function attachMembership() {
    document.querySelectorAll('.kn-membership').forEach(dialog => {
      if (membershipObserved.has(dialog)) return;
      membershipObserved.add(dialog);
      let wasOpen = false;
      const update = () => {
        const open = dialog.classList.contains('open');
        if (open && !wasOpen && allowed()) send('newsletter_interaction', {
          form_location: 'membership_popup', newsletter_action: 'open', method: 'unknown'
        });
        wasOpen = open;
      };
      new MutationObserver(update).observe(dialog, { attributes: true, attributeFilter: ['class'] });
      update();
    });
  }
  document.addEventListener('click', event => {
    if (!allowed() || event.button !== 0 || event.defaultPrevented) return;
    const parts = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const anchor = parts.find(el => el?.matches?.('a[href]')) || event.target.closest?.('a[href]');
    const account = parts.find(el => el?.matches?.('shopify-account')) || event.target.closest?.('shopify-account');
    if (anchor) {
      let destination;
      try { destination = new URL(anchor.href, location.origin); } catch (_) { return; }
      if (destination.origin !== location.origin || !/\/(?:pages\/knotnot-login|customer_authentication\/login|account\/login)\/?$/.test(destination.pathname)) return;
      const surface = anchor.closest('.kn-membership') ? 'membership_popup' : 'account_login';
      const provider = destination.searchParams.get('acr_values');
      const label = anchor.textContent || '';
      const method = provider === 'provider:google' || /google/i.test(label) ? 'google' :
        provider === 'provider:facebook' || /meta|facebook/i.test(label) ? 'facebook' : 'email';
      accountRecord(surface, method, true);
      send('newsletter_interaction', { form_location: surface, newsletter_action: 'login_click', method });
    } else if (account) {
      accountRecord('account_login', 'unknown', false);
      send('newsletter_interaction', { form_location: 'account_login', newsletter_action: 'open', method: 'unknown' });
    }
  }, true);
  function checkAccountReturn(attempt = 0) {
    const record = read('account_newsletter');
    if (!record) return;
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') {
      if (attempt < 40) setTimeout(() => checkAccountReturn(attempt + 1), 250);
      return;
    }
    if (!allowed() || !Number.isFinite(record.time) || Date.now() - record.time > 30 * 60 * 1000 || Date.now() < record.time ||
        !accountSurfaces.includes(record.surface) || !accountMethods.includes(record.method)) {
      remove('account_newsletter'); return;
    }
    // Record exists only when the originating storefront rendered an anonymous session.
    // Native account menus can bypass the local login bridge; server-authenticated return is sufficient.
    if (accountState()?.knGaAuth !== 'true') return;
    const marketing = accountState()?.knGaMarketing;
    if (!['true', 'false'].includes(marketing)) return;
    remove('account_newsletter');
    send('newsletter_interaction', {
      form_location: record.surface, method: record.method,
      newsletter_action: marketing === 'true' ? 'subscribed_login_return' : 'unsubscribed_login_return'
    }, record.id);
  }

  // Newsletter interactions and successful subscriptions use the same surface labels.
  const newsletterSurface = form => form.id?.startsWith('KnotFriends-') ? 'knotjustfriends' :
    form.matches('.newsletter-form') ? 'footer' : 'newsletter_section';
  document.addEventListener('click', event => {
    if (!allowed() || event.button !== 0) return;
    const opener = event.target.closest?.('promo-popup .open-sticky-tab');
    if (opener && opener.closest('promo-popup')?.querySelector('form[id^="KnotFriends-"]')) {
      send('newsletter_interaction', { form_location: 'knotjustfriends', newsletter_action: 'open', method: 'email' });
    }
  });
  const newsletterStarted = new WeakSet();
  document.addEventListener('focusin', event => {
    const form = event.target.closest?.('form.newsletter-form, form.section-newsletter-form');
    if (!allowed() || !form || newsletterStarted.has(form)) return;
    newsletterStarted.add(form);
    send('newsletter_interaction', { form_id: form.id || 'newsletter_signup', form_location: newsletterSurface(form), newsletter_action: 'start', method: 'email' });
  });
  // CAPTCHA may resume with native form.submit(), bypassing submit listeners.
  // Remember intent only; the next document must contain Shopify's success marker.
  function recordContactAttempt(form) {
    if (!allowed() || /(?:^|\/)challenge\/?$/.test(location.pathname) || !form?.id?.startsWith('ContactForm-') || !form.matches('form') || form.matches('.newsletter-form, .section-newsletter-form') ||
        !form.querySelector('[name="form_type"][value="contact"]') || !form.checkValidity()) return;
    const previous = read('generate_lead');
    if (previous?.document_id === documentId && previous.form_id === (form.id || 'generate_lead')) return;
    write('generate_lead', { id: uid(), time: Date.now(), form_id: form.id || 'generate_lead', source: location.pathname, document_id: documentId });
    if (debug) console.info('KNOTNOT_CONTACT_ATTEMPT_RECORDED');
  }
  document.addEventListener('click', event => {
    if (event.button !== 0) return;
    const button = event.target.closest?.('button, input[type="submit"]');
    if (button?.type === 'submit') recordContactAttempt(button.form);
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.tagName === 'INPUT') recordContactAttempt(event.target.form);
  }, true);
  // A submit is an interaction, never a successful subscription on its own.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!allowed() || !form.matches('form') || !form.checkValidity()) return;
    const kind = form.matches('.newsletter-form, .section-newsletter-form') ? 'newsletter_signup' :
      form.querySelector('[name="form_type"][value="contact"]') ? 'generate_lead' : null;
    if (!kind) return;
    if (kind === 'generate_lead') { recordContactAttempt(form); return; }
    const surface = kind === 'newsletter_signup' ? newsletterSurface(form) : location.pathname;
    write(kind, { id: uid(), time: Date.now(), form_id: form.id || kind, source: location.pathname, surface });
    if (kind === 'newsletter_signup') {
      send('newsletter_interaction', { form_id: form.id || kind, form_location: surface, newsletter_action: 'submit', method: 'email' });
    }
  }, true);
  const observedSuccess = new WeakSet();
  function checkSuccess() {
    document.querySelectorAll('[data-kn-ga-success], form.section-newsletter-form[id^="KnotFriends-"] [data-knot-newsletter-success]').forEach(node => {
      if (observedSuccess.has(node)) return;
      const kind = node.dataset.knGaSuccess || 'newsletter_signup';
      if (!['newsletter_signup', 'generate_lead'].includes(kind)) return;
      const record = read(kind);
      if (!record || Date.now() - record.time > 10 * 60 * 1000 || record.source !== location.pathname || !allowed()) return;
      if (kind === 'generate_lead' && record.document_id === documentId) return;
      const form = node.closest('form');
      if (!form || (form.id || kind) !== record.form_id) return;
      observedSuccess.add(node); remove(kind);
      send(kind === 'generate_lead' ? 'contact_form_submit' : kind, {
        form_id: record.form_id,
        form_location: kind === 'newsletter_signup' ? (record.surface || newsletterSurface(form)) : record.source,
        method: 'email'
      }, record.id);
    });
  }

  // Only recognized collection controls; never log arbitrary field values.
  document.addEventListener('change', event => {
    if (!allowed() || !location.pathname.includes('/collections/')) return;
    const input = event.target;
    if (input.name === 'sort_by' && input.tagName === 'SELECT') {
      const sorts = ['manual','best-selling','title-ascending','title-descending','price-ascending','price-descending','created-ascending','created-descending'];
      if (sorts.includes(input.value)) send('collection_sort', { sort_by: input.value, item_list_id: location.pathname });
    } else if (typeof input.name === 'string' && /^filter\.[a-zA-Z0-9_.-]+$/.test(input.name)) {
      send('collection_filter', { filter_name: input.name, filter_action: input.type === 'checkbox' ? (input.checked ? 'add' : 'remove') : 'change', item_list_id: location.pathname });
    }
  });

  function scan() { attachDrawers(); attachMembership(); checkSuccess(); }
  let scheduled = false;
  new MutationObserver(records => {
    if (scheduled || !records.some(r => r.addedNodes.length)) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  }).observe(document.documentElement, { subtree: true, childList: true });
  // Consent can become available after the first DOM scan on a server return.
  function retryFormSuccess(attempt = 0) {
    checkSuccess();
    if (attempt < 40 && (read('generate_lead') || read('newsletter_signup')))
      setTimeout(() => retryFormSuccess(attempt + 1), 250);
  }
  scan();
  retryFormSuccess();
  checkAccountReturn();
})();


